
import Icons from "@/app/ui/components/icons/index.vue";
import PaginationV3 from "@/app/ui/components/pagination-v3/index.vue";
import TableLazyLoad from "@/app/ui/components/lazy-load/table-lazy-load.vue";
import EmptyList from "@/app/ui/components/empty-list/index.vue";
import { Pagination } from "@/domain/entities/Pagination";
import { ListSummaryItem, RequestListSummary } from "@/domain/entities/PickupDashboardSummary";
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import type { PropType, CSSProperties } from "vue";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import { useRoute } from "vue-router";
import ErrorMessage from "@/app/ui/components/error-message/index.vue";

interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  style?: CSSProperties | Record<string, any>;
}

export default defineComponent({
  name: "TableSummary",

  components: {
    PaginationV3,
    TableLazyLoad,
    EmptyList,
    ErrorMessage
  },

  props: {
    tableData: {
      type: Array as PropType<ListSummaryItem[]>,
      required: true,
    },
   pagination: {
      type: Object as PropType<Pagination>,
      default: () => new Pagination(1, 10, 0),
    },
    startIndex: {
      type: Number,
      default: 0,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    enableSort: {
      type: Boolean,
      default: false
    },
    columns: {
      type: Array as PropType<TableColumn[]>,
      required: true,
      default: () => [],
    },
    sortKey: {
      type: String as PropType<string | null>,
      default: null,
    },
    sortOrder: {
      type: String as PropType<"asc" | "desc" | null>,
      default: null,
    },
    substractHeight: {
      type: String as PropType<string>,
      default: "365",
    }
  },

  emits: ["sort", "set-page", "select-all", "select-row"],

  setup(props, {emit}) {
    const selectedRows = ref<ListSummaryItem[]>([]);

    const effectiveSubtractHeight = computed(() => {
      return selectedRows.value && selectedRows.value.length ? 410 : Number(props.substractHeight || 365);
    });

    const route = useRoute();

    const lastSegment = computed(() => route.path.split("/").pop());

    const isRestrictedTab = computed(() => {
      return lastSegment.value === "crrdon" || lastSegment.value === "crrpic";
    });


    const getRemainingSeconds = (datetime: string) => {
      const target = new Date(datetime).getTime();
      return Math.floor((target - Date.now()) / 1000);
    };

    const formatRemaining = (datetime: string) => {
      const diff = getRemainingSeconds(datetime);
      if (diff <= 0) return "00:00:00";

      const h = String(Math.floor(diff / 3600)).padStart(2, "0");
      const m = String(Math.floor((diff % 3600) / 60)).padStart(2, "0");
      const s = String(diff % 60).padStart(2, "0");

      return `${h}:${m}:${s}`;
    };

    const getTimeColor = (datetime: string) => {
      const diff = getRemainingSeconds(datetime);
      if (diff <= 0) return "bg-red-lp-1300 text-red-lp-2000";
      if (diff < 30 * 60) return "bg-red-lp-1300 text-red-lp-2000";
      return "bg-gray-lp-1000";
    };

    const tick = ref(0);
    let timer: number;

    onMounted(() => {
      timer = window.setInterval(() => tick.value++, 1000);
    });

    onBeforeUnmount(() => clearInterval(timer));

    watch(
      () => PickupDashboardController.currentFilter.q,
      (newValue, oldValue) => {
        if (oldValue) selectedRows.value = [];
      }
    );

    // Watch for changes in controller's selectedRowsPickup to sync local state
    watch(
      () => PickupDashboardController.selectedRowsPickup,
      (newSelectedRows) => {
        selectedRows.value = [...newSelectedRows];
      },
      { deep: true }
    );

    const onSort = (col: TableColumn) => {
      if (!props.enableSort || col.sortable === undefined) return;

      let nextOrder: "asc" | "desc" = "asc";

      if (props.sortKey === col.key) {
        nextOrder = props.sortOrder === "asc" ? "desc" : "asc";
      }

      emit("sort", {
        key: col.key,
        order: nextOrder,
      });
    };

    const isSelected = (item: ListSummaryItem) =>
      selectedRows.value.some(v => v.incrementId === item.incrementId);

    const selectableRows = computed(() => {
      if (!isRestrictedTab.value) return props.tableData;
      return props.tableData.filter(item => item.isAbleToBooking);
    });

    const isAllSelected = computed(() => {
      const rows = selectableRows.value;
      return rows.length > 0 && rows.every(item => isSelected(item));
    });


    const toggleSelectAll = () => {
      const rows = selectableRows.value;
      if (!rows.length) return;

      if (isAllSelected.value) {
        const ids = rows.map(i => i.incrementId);
        selectedRows.value = selectedRows.value.filter(
          v => !ids.includes(v.incrementId)
        );
      } else {
        selectedRows.value = Array.from(
          new Map(
            [...selectedRows.value, ...rows].map(i => [i.incrementId, i])
          ).values()
        );
      }

      emit("select-all", selectedRows.value);
    };


    const toggleRow = (item: ListSummaryItem) => {
      if (isRestrictedTab.value && !item.isAbleToBooking) return;

      const exists = isSelected(item);

      if (exists) {
        selectedRows.value = selectedRows.value.filter(
          v => v.incrementId !== item.incrementId
        );
      } else {
        selectedRows.value = [...selectedRows.value, item];
      }

      emit("select-row", selectedRows.value);
    };

    const onSetPage = (page: number) => {
      selectedRows.value = []
      emit("set-page", page)
    }

    const getCellValue = (item: ListSummaryItem, key: string) => {
      return (item as any)[key];
    };

    const errorCause = computed(() => PickupDashboardController.errorCause)

    const isFiltering = computed(() => {
      const curentFilter = PickupDashboardController.currentFilter;
      return (
        !!curentFilter.q ||
        !!curentFilter.shipmentType ||
        !!curentFilter.startDate ||
        !!curentFilter.endDate
      );
    });

    const fetchListSummary = async () => {
      const filterResult = PickupDashboardController.currentFilter;
      await PickupDashboardController.getListSummary(
        new RequestListSummary({
          status: filterResult.status,
          orderBy: filterResult.orderBy,
          sortBy: filterResult.sortBy,
        })
      );
    };

    return {
      selectedRows,
      isAllSelected,
      tick,
      toggleSelectAll,
      formatRemaining,
      getTimeColor,
      onSort,
      toggleRow,
      onSetPage,
      getCellValue,
      isSelected,
      isFiltering,
      isRestrictedTab,
      effectiveSubtractHeight,
      errorCause,
      fetchListSummary
    };
  },
});
